import { RotateCw, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import type { OpMeta, SchemaProp } from '@/catalog/types'
import { Combobox, type Source } from '@/components/Combobox'
import { DetailPane, DetailSection, KeyValue } from '@/components/data/DetailPane'
import { Chip, FilterBar } from '@/components/data/FilterBar'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { StatusText } from '@/components/data/StatusText'
import { FindingsTable } from '@/components/FindingsTable'
import { Page } from '@/components/layout/Page'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { cellText } from '@/lib/findings'
import { useOperationsStatus } from '@/lib/apiCatalog'
import { fetchOps } from '@/lib/ops'
import { pushRecent } from '@/lib/opPrefs'
import { deletePreset, listPresets, savePreset, type Preset } from '@/lib/presets'
import { ApiStatusBanner } from '@/pages/Operations/ApiStatusBanner'
import { OpCatalogRail, OpCatalogRailCollapsed } from '@/pages/Operations/OpCatalogRail'
import { PreviewPane, type OpResult } from '@/pages/Operations/PreviewPane'

function coerce(t: string | undefined, isArray: boolean, raw: string): unknown {
  if (isArray) {
    // An untouched textarea must NOT serialize to [] — for `list | None` backend params
    // an empty list means "replace with nothing" (wipe), not "leave unchanged".
    const items = raw.split('\n').map((s) => s.trim()).filter(Boolean)
    return items.length > 0 ? items : undefined
  }
  if (t === 'integer' || t === 'number') return raw === '' ? undefined : Number(raw)
  // An untouched boolean is omitted, never sent as false — the model default applies.
  if (t === 'boolean') {
    const b = raw.trim().toLowerCase() // deep links may say True / 1
    return b === 'true' || b === '1' ? true : b === 'false' || b === '0' ? false : undefined
  }
  return raw === '' ? undefined : raw
}

// Form text → the boolean select's value ('' | 'true' | 'false').
function boolSelectValue(raw: string): string {
  const b = coerce('boolean', false, raw)
  return b === undefined ? '' : String(b)
}

// A scalar schema default as form text (null / objects / arrays are not shown).
function defaultText(sch: SchemaProp): string | undefined {
  const d = sch.default
  return typeof d === 'string' || typeof d === 'number' || typeof d === 'boolean' ? String(d) : undefined
}

// Resolve a field's effective type through `anyOf` — optional fields are
// `anyOf: [{type:array,items}|{type:integer}|…, {type:null}]` with no top-level `type`.
function effectiveType(sch: SchemaProp): { type?: string; isArray: boolean; itemType?: string } {
  if (sch.type === 'array') return { type: 'array', isArray: true, itemType: sch.items?.type }
  const arr = sch.anyOf?.find((v) => v.type === 'array')
  if (arr) return { type: 'array', isArray: true, itemType: arr.items?.type }
  const scalar = sch.anyOf?.find((v) => v.type && v.type !== 'null')
  return { type: scalar?.type ?? sch.type, isArray: false }
}

// Inverse of coerce: a re-run's canonical params → the form's string values.
function prefillToValues(p: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(p)) {
    if (k === 'dry_run' || k === 'confirm_token') continue
    out[k] = Array.isArray(v) ? v.join('\n') : v == null ? '' : String(v)
  }
  return out
}

/** One parameter as `name: value` for the collapsed parameter line. */
function paramText(v: unknown): string {
  if (typeof v === 'string') return JSON.stringify(v)
  if (Array.isArray(v)) return `[${v.map((x) => (typeof x === 'string' ? JSON.stringify(x) : String(x))).join(', ')}]`
  return String(v)
}

function typeChip(meta: OpMeta): string {
  if (meta.critical) return 'critical · confirm'
  if (!meta.read_only) return 'write · preview → confirm'
  return 'read-only'
}

const H = 'text-[11px] font-medium uppercase tracking-[0.07em] text-text-3'

/** Remount per operation: a link from the rail starts with a clean form and result. */
export default function OperationPage() {
  const { operationId } = useParams<{ operationId: string }>()
  return <OperationView key={operationId} operationId={operationId ?? ''} />
}

function OperationView({ operationId }: { operationId: string }) {
  const [ops, setOps] = useState<OpMeta[]>([])
  const [meta, setMeta] = useState<OpMeta | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const [reloadTick, setReloadTick] = useState(0)
  const [values, setValues] = useState<Record<string, string>>({})
  const [preview, setPreview] = useState<OpResult | null>(null)
  const [result, setResult] = useState<OpResult | null>(null)
  // the shown result is in the audit log: only an applied mutation (execute, not a preview,
  // not read-only — see backend api/_runner.py should_audit) is recorded there
  const [audited, setAudited] = useState(false)
  // bumped on every form edit: a preview answer that arrives after an edit is stale
  const editSeq = useRef(0)
  // the parameters the shown read-only result was fetched with
  const [ranWith, setRanWith] = useState<Record<string, unknown>>({})
  const [err, setErr] = useState<string | null>(null)
  const [executing, setExecuting] = useState(false)
  const [running, setRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [presets, setPresets] = useState<Preset[]>([])
  const [presetName, setPresetName] = useState('')
  const [loadedPreset, setLoadedPreset] = useState('')
  const [railOpen, setRailOpen] = useState(true)
  const [editing, setEditing] = useState(false)
  const [row, setRow] = useState<string | null>(null)
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const apiStatus = useOperationsStatus()

  // Load the op's metadata from /api/ops (the whole registry, unfiltered). Three outcomes
  // must stay distinct — conflating them is what made a missing op hang on "loading…" forever:
  //   • fetch in flight        → meta=null, loadErr=null  (show the spinner)
  //   • op absent from registry → loadErr='not-found'      (usually a stale backend build)
  //   • fetch failed            → loadErr=<message>        (backend unreachable, etc.)
  useEffect(() => {
    let cancelled = false
    setMeta(null)
    setLoadErr(null)
    fetchOps()
      .then((list) => {
        if (cancelled) return
        const all = Array.isArray(list) ? list : []
        setOps(all)
        const found = all.find((o) => o.operation_id === operationId)
        if (found) setMeta(found)
        else setLoadErr('not-found')
      })
      .catch((e: Error) => {
        if (!cancelled) setLoadErr(e.message || 'failed to load operations')
      })
    return () => {
      cancelled = true
    }
  }, [operationId, reloadTick])

  useEffect(() => {
    if (operationId) listPresets(operationId).then(setPresets).catch(() => setPresets([]))
  }, [operationId])

  // Re-run: seed the form once from params passed via location.state (Audit Run "Re-run").
  // Deep-link prefill: also seed from URL query params (?account_id=xxx&...), state takes precedence.
  useEffect(() => {
    const statePrefill = (location.state as { prefill?: Record<string, unknown> } | null)?.prefill
    const queryPrefill: Record<string, string> = {}
    searchParams.forEach((value, key) => {
      queryPrefill[key] = value
    })
    const merged = { ...queryPrefill, ...(statePrefill ? prefillToValues(statePrefill) : {}) }
    if (Object.keys(merged).length > 0) setValues(merged)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (loadErr) {
    const notFound = loadErr === 'not-found'
    return (
      <Page
        crumb={<><Link to="/operations" className="hover:text-foreground">Operations</Link><span aria-hidden>/</span></>}
        title={notFound ? 'Operation not available' : 'Couldn’t load operation'}
      >
        <div className="px-4 py-4">
          <p className="max-w-2xl text-[13px] leading-relaxed text-text-2">
            {notFound ? (
              <>
                <code className="rounded bg-surface px-1 py-0.5 font-mono text-[12.5px] text-foreground">
                  {operationId}
                </code>{' '}
                isn’t registered in the running backend. If it was added recently, the backend is
                likely serving an older build — restart it, then retry. Otherwise the operation may
                have been renamed or removed.
              </>
            ) : (
              <>
                <span className="font-medium text-danger">failed</span> Failed to load the operations
                list: {loadErr}. Check that the backend is reachable, then retry.
              </>
            )}
          </p>
          <div className="mt-4 flex items-center gap-2">
            <Link to="/operations" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
              ← All operations
            </Link>
            <Button size="sm" onClick={() => setReloadTick((t) => t + 1)}>
              Retry
            </Button>
          </div>
        </div>
      </Page>
    )
  }
  if (!meta) {
    return (
      <Page title={<span className="font-mono">{operationId}</span>} hint={operationId}>
        <LoadingRows label="Loading operation" rows={6} />
      </Page>
    )
  }

  const props = meta.input_schema.properties ?? {}
  const ns = meta.operation_id.slice(0, meta.operation_id.indexOf('.') + 1)
  const action = meta.operation_id.slice(meta.operation_id.indexOf('.') + 1)
  // read-only after Run: the form folds into a parameter line, results take the width
  const folded = meta.read_only && result !== null

  // Any edit after a Preview discards it (and its confirm token): Apply / Confirm must never
  // send parameters that were not previewed. A read-only result stays — it shows ranWith.
  function changeValues(next: Record<string, string>) {
    setValues(next)
    editSeq.current += 1
    if (preview) setPreview(null)
    if (!meta!.read_only && result) {
      setResult(null)
      setAudited(false)
    }
  }

  function buildBody(execute: boolean): Record<string, unknown> {
    const b: Record<string, unknown> = {}
    for (const [name, sch] of Object.entries(props)) {
      const et = effectiveType(sch)
      const v = coerce(et.isArray ? et.itemType : et.type, et.isArray, values[name] ?? '')
      if (v !== undefined) b[name] = v
    }
    b.dry_run = !execute
    if (execute && preview?.confirm_token) b.confirm_token = preview.confirm_token
    return b
  }

  // Canonical input params for a preset — buildBody without the dispatch-only keys.
  function presetParams(): Record<string, unknown> {
    const b = buildBody(false)
    delete b.dry_run
    delete b.confirm_token
    return b
  }

  async function doSavePreset() {
    const name = presetName.trim()
    if (!name) return
    await savePreset(meta!.operation_id, name, presetParams())
    setPresetName('')
    setPresets(await listPresets(meta!.operation_id))
  }

  async function doDeletePreset(name: string) {
    await deletePreset(meta!.operation_id, name)
    setLoadedPreset('')
    setPresets(await listPresets(meta!.operation_id))
  }

  async function submit(execute: boolean) {
    setErr(null)
    pushRecent(meta!.operation_id)
    const body = buildBody(execute)
    if (execute) {
      const seq = editSeq.current
      setExecuting(true)
      setProgress(8)
      const iv = setInterval(() => setProgress((p) => Math.min(90, p + 7)), 120)
      try {
        const r = await api<OpResult>(`/ops/${meta!.operation_id}`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
        // a read-only op has no preview step: whatever comes back is its result
        if (!meta!.read_only && (r.status === 'awaiting_confirm' || r.status === 'preview')) {
          // the confirm step is for the parameters sent — an edit since then voids it
          if (seq !== editSeq.current) return
          setPreview(r)
          setResult(null)
          setAudited(false)
        } else {
          // A failed answer to Apply without a token may be a failed preview of the confirm
          // gate (never audited) — no link rather than a link to a missing run.
          setAudited(
            !meta!.read_only
            && r.status !== 'awaiting_approval'
            && !(r.status === 'failed' && !body.confirm_token),
          )
          if (meta!.read_only) {
            const params = { ...body }
            delete params.dry_run
            delete params.confirm_token
            setRanWith(params)
            setRow(null)
            setEditing(false)
            setRailOpen(false)
          }
          setResult(r)
          setPreview(null)
          if (r.atlassian_silent_failure) {
            toast.warning('Acknowledged but not applied — verify manually.')
          } else if (r.status === 'partial') {
            const n = r.changes.length
            toast.warning(`Partial: ${n} change${n === 1 ? '' : 's'} applied — check warnings.`)
          } else if (r.status === 'success' && !meta!.read_only) {
            // a read-only run applies nothing — its findings are on screen, no toast
            const n = r.changes.length
            toast.success(`${action} applied${n > 0 ? ` · ${n} change${n === 1 ? '' : 's'}` : ''}`)
          }
        }
      } catch (e) {
        const msg = (e as Error).message
        setErr(msg)
        toast.error(msg)
      } finally {
        clearInterval(iv)
        setProgress(100)
        setExecuting(false)
      }
    } else {
      setRunning(true)
      const seq = editSeq.current
      try {
        const r = await api<OpResult>(`/ops/${meta!.operation_id}`, {
          method: 'POST',
          body: JSON.stringify(body),
        })
        if (seq !== editSeq.current) return
        if (r.status === 'awaiting_confirm' || r.status === 'preview') {
          setPreview(r)
          setResult(null)
        } else {
          // a dry run (e.g. a failed preview) is never written to the audit log
          setResult(r)
          setPreview(null)
        }
        setAudited(false)
      } catch (e) {
        setErr((e as Error).message)
      } finally {
        setRunning(false)
      }
    }
  }

  const busy = executing || running
  const mainLabel = meta.read_only
    ? (executing ? 'Running…' : 'Run')
    : (running ? 'Previewing…' : 'Preview')

  const fields: ReactNode = (
    <>
      {Object.entries(props).length === 0 && (
        <p className="text-[13px] text-text-2">No inputs — run to fetch results.</p>
      )}
      {Object.entries(props).map(([name, sch]) => {
        const et = effectiveType(sch)
        return (
          <div key={name} className="min-w-0">
            <Label htmlFor={name} className="mb-1 font-mono text-[12px] font-normal text-text-2">
              {name}
            </Label>
            {sch.source ? (
              <Combobox
                source={sch.source as Source}
                valueKey={sch.value_key}
                deps={Object.fromEntries((sch.depends_on ?? []).map((d) => [d, values[d] ?? '']))}
                multi={et.isArray}
                ariaLabel={name}
                value={values[name] ?? ''}
                onChange={(v) => changeValues({ ...values, [name]: v })}
              />
            ) : et.type === 'boolean' ? (
              <select
                id={name}
                value={boolSelectValue(values[name] ?? '')}
                onChange={(e) => changeValues({ ...values, [name]: e.target.value })}
                className="h-8 w-full rounded-lg border border-input bg-surface px-2 font-mono text-[12.5px] text-foreground outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50"
              >
                <option value="">{defaultText(sch) !== undefined ? `default (${defaultText(sch)})` : 'not set'}</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            ) : et.isArray ? (
              <Textarea
                id={name}
                rows={5}
                placeholder="one per line"
                value={values[name] ?? ''}
                onChange={(e) => changeValues({ ...values, [name]: e.target.value })}
                className="font-mono text-[12.5px]"
              />
            ) : (
              <Input
                id={name}
                type={et.type === 'integer' || et.type === 'number' ? 'number' : 'text'}
                placeholder={defaultText(sch) !== undefined ? `default: ${defaultText(sch)}` : undefined}
                value={values[name] ?? ''}
                onChange={(e) => changeValues({ ...values, [name]: e.target.value })}
                className="font-mono text-[12.5px] md:text-[12.5px]"
              />
            )}
          </div>
        )
      })}
      {meta.operation_id === 'fields.add_options_to_context' && values.field_id && values.context_id && (
        <OptionsReference fieldId={values.field_id} contextId={values.context_id} />
      )}
    </>
  )

  const mainButton = (
    <div className="flex flex-wrap items-center gap-3">
      <Button onClick={() => submit(meta.read_only)} disabled={busy}>
        {mainLabel}
      </Button>
      {!meta.read_only && <span className="text-[12px] text-text-3">Nothing changes until you confirm.</span>}
      {meta.read_only && executing && (
        <div className="progress-track w-40" role="progressbar" aria-label="Running" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      )}
    </div>
  )
  const errLine = err && (
    <p role="alert" className="text-[12.5px] text-text-2">
      <span className="font-medium text-danger">failed</span> {err}
    </p>
  )

  const presetsControl = (
    <div className="flex items-center gap-1.5">
      {presets.length > 0 && (
        <>
          <select
            aria-label="Load preset"
            value={loadedPreset}
            onChange={(e) => {
              const p = presets.find((x) => x.name === e.target.value)
              setLoadedPreset(e.target.value)
              if (p) changeValues(prefillToValues(p.params))
            }}
            className="h-7 max-w-40 rounded-md border border-line-strong bg-surface px-2 font-mono text-[12px] text-foreground"
          >
            <option value="" disabled>load preset…</option>
            {presets.map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          {loadedPreset && (
            <button
              type="button"
              onClick={() => doDeletePreset(loadedPreset)}
              aria-label={`Delete preset ${loadedPreset}`}
              title={`Delete preset ${loadedPreset}`}
              className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-raised hover:text-danger"
            >
              <Trash2 aria-hidden className="size-3.5" />
            </button>
          )}
        </>
      )}
      <input
        value={presetName}
        onChange={(e) => setPresetName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') doSavePreset() }}
        placeholder="preset name"
        aria-label="Preset name"
        className="h-7 w-32 rounded-md border border-line-strong bg-surface px-2 font-mono text-[12px] text-foreground placeholder:text-text-3"
      />
      <button
        type="button"
        disabled={!presetName.trim()}
        onClick={doSavePreset}
        className="h-7 rounded-md border border-line-strong bg-surface-raised px-2.5 text-[12px] text-text-2 transition-colors enabled:hover:text-foreground disabled:opacity-50"
      >
        Save preset
      </button>
    </div>
  )

  const findings = result?.findings ?? []
  const selected = row !== null ? findings[Number(row)] : undefined
  const paramEntries = Object.entries(ranWith)

  return (
    <Page
      crumb={<><Link to="/operations" className="hover:text-foreground">Operations</Link><span aria-hidden>/</span></>}
      title={
        <span className="font-mono text-[14px]">
          <span className="font-normal text-text-3">{ns}</span>
          <span className="text-foreground">{action}</span>
        </span>
      }
      hint={`${meta.operation_id} — ${meta.summary}`}
      context={
        <>
          <Chip>{typeChip(meta)}</Chip>
          <span className="min-w-0 truncate text-[12.5px] text-text-2" title={meta.summary}>{meta.summary}</span>
        </>
      }
      actions={presetsControl}
    >
      {apiStatus[meta.operation_id] && (
        <div className="shrink-0 border-b border-line px-4 py-2.5">
          <ApiStatusBanner status={apiStatus[meta.operation_id]} className="" />
        </div>
      )}

      {folded && (
        <div className="shrink-0 border-b border-line">
          <div className="flex min-h-10 items-center gap-3 px-4 py-1.5">
            <span className={H}>Parameters</span>
            <p aria-label="Parameters" className="min-w-0 flex-1 truncate font-mono text-[12px] text-text-2">
              {paramEntries.length === 0
                ? <span className="text-text-3">no parameters</span>
                : paramEntries.map(([k, v], i) => (
                  <span key={k}>
                    {i > 0 && <span className="text-text-3"> · </span>}
                    <span className="text-text-3">{k}:</span> <span className="text-foreground">{paramText(v)}</span>
                  </span>
                ))}
            </p>
            <Button variant="outline" size="sm" aria-expanded={editing} onClick={() => setEditing((e) => !e)}>
              Edit
            </Button>
            <Button
              size="sm"
              onClick={() => submit(true)}
              disabled={busy}
              aria-label="Run again"
              title="Run again with these parameters"
            >
              Run <RotateCw aria-hidden className={executing ? 'animate-spin' : ''} />
            </Button>
          </div>
          {editing && (
            <div className="space-y-3 border-t border-line bg-pane px-4 py-3">
              <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-x-4 gap-y-3">{fields}</div>
              {mainButton}
              {errLine}
            </div>
          )}
        </div>
      )}

      <div className="flex min-h-0 flex-1">
        {railOpen
          ? <OpCatalogRail ops={ops} currentId={meta.operation_id} onHide={() => setRailOpen(false)} />
          : <OpCatalogRailCollapsed onShow={() => setRailOpen(true)} />}

        {folded ? (
          <div className="flex min-w-0 flex-1 flex-col">
            <FilterBar>
              <StatusText status={result.status} />
              <span>
                <span className="font-mono tabular-nums text-foreground">{findings.length}</span>{' '}
                {findings.length === 1 ? 'finding' : 'findings'}
              </span>
              {result.warnings.length > 0 && (
                <span title={result.warnings.join('\n')} className="min-w-0 max-w-[50%] truncate">
                  <span className="font-medium text-warn">{result.warnings.length === 1 ? 'warning' : `${result.warnings.length} warnings`}</span>{' '}
                  {result.warnings.join('; ')}
                </span>
              )}
              {result.atlassian_silent_failure && (
                <span><span className="font-medium text-danger">not applied</span> verify manually</span>
              )}
              {err && !editing && <span role="alert"><span className="font-medium text-danger">failed</span> {err}</span>}
            </FilterBar>
            <SplitView
              list={<FindingsTable findings={findings} selectedId={row} onSelect={setRow} />}
              detail={selected ? <FindingDetail finding={selected} index={Number(row)} /> : null}
              onCloseDetail={() => setRow(null)}
              emptyDetail="Select a finding to see all its fields."
            />
          </div>
        ) : (
          <>
            <section aria-label="Inputs" className="min-w-0 flex-1 overflow-y-auto px-5 py-4">
              {/* inputs read best at a form's width; the preview column takes the rest */}
              <div className="max-w-[720px] space-y-3.5">
                <h2 className={H}>Inputs</h2>
                {fields}
                {mainButton}
                {errLine}
              </div>
            </section>
            <aside aria-label="Preview" className="flex w-[400px] shrink-0 flex-col overflow-y-auto border-l border-line bg-pane">
              {meta.read_only
                ? <p className="px-4 py-3.5 text-[12.5px] text-text-3">Run to see results here.</p>
                : (
                  <PreviewPane
                    preview={preview}
                    result={result}
                    audited={audited}
                    critical={meta.critical}
                    executing={executing}
                    progress={progress}
                    onConfirm={() => submit(true)}
                  />
                )}
            </aside>
          </>
        )}
      </div>
    </Page>
  )
}

function FindingDetail({ finding, index }: { finding: Record<string, unknown>; index: number }) {
  const entries = Object.entries(finding)
  const first = entries.find(([, v]) => typeof v === 'string' || typeof v === 'number')
  return (
    <DetailPane
      eyebrow={`Finding ${index + 1}`}
      title={<span className="font-mono text-[13px]">{first ? cellText(first[1]) : `#${index + 1}`}</span>}
    >
      <DetailSection title="Fields">
        <KeyValue
          items={entries.map(([k, v]): [string, ReactNode] => [
            k,
            v !== null && typeof v === 'object'
              ? (
                <pre className="max-h-64 overflow-auto rounded-md bg-surface p-2 font-mono text-[11.5px] leading-relaxed text-text-2">
                  {JSON.stringify(v, null, 2)}
                </pre>
              )
              : <span className="font-mono text-[12px]">{v === null || v === undefined ? '—' : String(v)}</span>,
          ])}
        />
      </DetailSection>
    </DetailPane>
  )
}

function OptionsReference({ fieldId, contextId }: { fieldId: string; contextId: string }) {
  const [opts, setOpts] = useState<{ option_id: string; value: string }[] | null>(null)
  const [err, setErr] = useState(false)
  useEffect(() => {
    setOpts(null)
    setErr(false)
    api<{ option_id: string; value: string }[]>(
      `/cache/fields/${encodeURIComponent(fieldId)}/contexts/${encodeURIComponent(contextId)}/options`,
    )
      .then(setOpts)
      .catch(() => setErr(true))
  }, [fieldId, contextId])
  return (
    <section aria-label="Existing options" className="border-t border-line pt-3">
      <h3 className={H}>Existing options {opts && <span className="font-mono normal-case tracking-normal">{opts.length}</span>}</h3>
      {err && (
        <p className="mt-1 text-[12px] text-text-2"><span className="font-medium text-danger">failed</span> could not load existing options</p>
      )}
      {opts && opts.length === 0 && <p className="mt-1 text-[12px] text-text-3">none yet</p>}
      {opts && opts.length > 0 && (
        <ul className="mt-1.5 flex max-h-40 flex-wrap gap-1 overflow-y-auto">
          {opts.map((o) => (
            <li key={o.option_id} className="rounded bg-surface-active px-1.5 py-0.5 font-mono text-[11.5px] text-text-2">
              {o.value}
            </li>
          ))}
        </ul>
      )}
      {!opts && !err && <p className="mt-1 text-[12px] text-text-3">loading…</p>}
    </section>
  )
}
